import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import MagneticButton from "./MagneticButton";
import AnimatedCounter from "./AnimatedCounter";
import { useLanguage } from "../context/LanguageContext";

export default function Hero() {
  const { t, dir, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollY, scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 45, damping: 18, mass: 0.5 });
  const yBg = useTransform(smoothProgress, [0, 1], [0, 220]);
  const yGlow = useTransform(smoothProgress, [0, 1], [0, -140]);
  const xGlow = useTransform(smoothProgress, [0, 1], [0, 70]);
  const yText = useTransform(smoothProgress, [0, 1], [0, -120]);
  const scaleBg = useTransform(smoothProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(smoothProgress, [0, 0.8], [1, 0]);
  const textParallax = useTransform(scrollY, [0, 420], [0, -40]);
  const subParallax = useTransform(smoothProgress, [0, 1], [0, -30]);
  const statsParallax = useTransform(smoothProgress, [0, 1], [0, -24]);

  return (
    <section
      ref={ref}
      dir={dir}
      lang={lang}
      className="relative min-h-[100svh] w-full overflow-hidden grain-overlay pt-24 sm:pt-28"
      data-testid="hero-section"
    >
      {/* Parallax bg */}
      <motion.div
        style={{ y: yBg, scale: scaleBg }}
        className="absolute inset-0 -z-10"
        aria-hidden
      >
        <img
          src="https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt=""
          className="h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_50%_30%,rgba(212,175,55,0.18),transparent_70%)]" />
      </motion.div>

      <motion.div
        style={{ y: yGlow, x: xGlow, opacity: smoothProgress }}
        className="pointer-events-none absolute left-1/2 top-1/4 -z-10 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-agency-gold/10 blur-3xl"
        aria-hidden
      />

      {/* Decorative orbiting ring */}
      <div className="pointer-events-none absolute -right-40 -top-40 h-[40rem] w-[40rem] rounded-full border border-agency-gold/10 animate-spin-slow" />
      <div className="pointer-events-none absolute -right-24 top-24 h-[28rem] w-[28rem] rounded-full border border-agency-gold/15 animate-spin-slow [animation-direction:reverse]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-12 pb-24">
        <motion.div style={{ y: yText }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-agency-gold/30 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.25em] text-agency-gold"
            data-testid="hero-badge"
          >
            <Sparkles size={14} />
            <span>Salama Solutions</span>
            <span className="text-white/30">/</span>
            <span className="text-white/60">{t.hero.badgeStudio}</span>
          </motion.div>

          <motion.h1
            style={{ y: textParallax, opacity }}
            className="mt-6 sm:mt-8 font-display text-[40px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[88px] font-black tracking-[-0.03em] text-balance max-w-5xl"
            data-testid="hero-headline"
          >
            <Reveal delay={0.05}>{t.hero.headlineLine1}</Reveal>
            <br />
            <Reveal delay={0.18}>
              <span className="gradient-gold-text">{t.hero.headlineLine2Gold}</span>{" "}
              <span className="text-white/85">{t.hero.headlineLine2Rest}</span>
            </Reveal>
            <br />
            <Reveal delay={0.28}>
              <span className="font-serif-display italic font-light text-white/95">
                {t.hero.headlineLine3}
              </span>
            </Reveal>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            style={{ y: subParallax, opacity }}
            className="mt-6 sm:mt-8 max-w-2xl text-sm sm:text-base md:text-lg text-white/65 leading-relaxed"
            data-testid="hero-subheadline"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
          >
            <MagneticButton>
              <Link
                to="/demos"
                className="group inline-flex w-full sm:w-auto items-center justify-center sm:justify-start gap-3 bg-agency-gold text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-white transition-colors"
                data-testid="hero-cta-view-demos"
              >
                {t.hero.ctaDemos}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </Link>
            </MagneticButton>
            <MagneticButton>
              <Link
                to="/contact"
                className="inline-flex w-full sm:w-auto items-center justify-center sm:justify-start gap-3 border border-white/20 text-white font-semibold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:border-agency-gold hover:text-agency-gold transition-colors"
                data-testid="hero-cta-contact"
              >
                {t.hero.ctaContact}
              </Link>
            </MagneticButton>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            style={{ y: statsParallax, opacity }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-8 max-w-3xl"
          >
            <Stat number={<AnimatedCounter to={40} suffix="+" />} label={t.hero.statProjects} />
            <Stat number={<AnimatedCounter to={3} suffix="" />} label={t.hero.statNiches} />
            <Stat number={<AnimatedCounter to={98} suffix="%" />} label={t.hero.statRetention} />
            <Stat number={<AnimatedCounter to={7} suffix="d" />} label={t.hero.statDelivery} />
          </motion.div>
        </motion.div>
      </div>

      {/* scroll cue */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] uppercase tracking-[0.3em]">{lang === "ar" ? "مرر للأسفل" : "Scroll"}</span>
        <span className="block h-10 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
}

function Reveal({ children, delay = 0 }) {
  return (
    <span className="inline-block overflow-hidden align-bottom">
      <motion.span
        className="inline-block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl md:text-4xl font-black gradient-gold-text">
        {number}
      </div>
      <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.2em] text-white/50">{label}</div>
    </div>
  );
}
